/**
 * ComponentBoundary — V103-4-5, the boundary editor in a program's Scope tab
 * (ADR-0066 + amendments A1–A3; design: v103-programs-ui round 4, screen B).
 *
 *   - One workspace component register; each program has its own scope. Only
 *     THIS program's column is editable (a DS Switch, In / Out text carries
 *     the state); the other program's column is read-only with "Edit in <it>".
 *   - Every scope change raises a Toast with Undo (and a row Undo). The toast
 *     renders before focus moves and keeps focused content clear of it
 *     (build acceptance #2).
 *   - Suggestions from connected clouds: split buttons whose default follows
 *     the federal-data hint; "Add N as suggested" is the filled primary with
 *     an Undo toast (reopens the suggestions, ends their components).
 *   - Drift and gaps with an action on every row (no dead ends): "Remove from
 *     boundary" asks which program, "Plan a replacement" opens the
 *     single-component replacement, "Fill in N", "Mark retired".
 *   - Opens on an open (pre-Audit) period when one exists, else on the
 *     standing boundary (certified + ConMon is the common case), which is
 *     editable. A period whose audit started is frozen: read-only, labelled
 *     in the selector, with the way back to the editable boundary. `?from=class-a` (the FedRAMP checklist) opens the
 *     issues filtered to this program with a way back.
 *
 *   - V105-3-6 (FedRAMP / GovRAMP, manifest `features.transitionGuidance`): the services the
 *     product depends on (repo scan, connections, SBOMs, sign-in apps, network logs, questions)
 *     reviewed in `?view=inventory` (InfraInventoryReview): add / link / dismiss, federal data.
 *   - V105-4-6: their verdicts (Replace / Partition / Documented / Out of scope) in
 *     `?view=verdicts` (InfraVerdictBoard), with the five answers and the next step per row.
 *     After it, `?view=strategies` (InfraStrategyCompare, V105-5-5): the four strategies and the signed choice.
 *     After that, `?view=plan` (InfraPlanView, V105-6-7): the target plan, its five parts and the signed package.
 *
 * Reads (any member): boundary/components, scope, suggestions, drift,
 *   replacements; external-services (names). Writes (program lead or
 *   owner/admin; the server enforces, 403 shown inline): scope add / remove,
 *   suggestions accept / link / dismiss / reopen, components create / update / end.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, EmptyState, Field, Input, Select, Spinner, Switch, Toast, ToastLiveRegion } from '@aigovops/design-system';
import { getProgramManifest, type BoundaryComponent, type BoundarySuggestion, type ProgramManifest, type ReplacementItem } from '@aigovops/core';
import { getToday, listProgramExternalServices, type ProgramCard } from '../../lib/api';
import {
  acceptSuggestion, addToScope, createComponent, dismissSuggestion, endComponent, getDrift, getScope, linkSuggestion, listComponents,
  listReplacements, listSuggestions, removeFromScope, reopenSuggestion, updateComponent, verifyExternalService,
} from '../../lib/boundary-api';
import {
  addChoiceLabel, componentsUsing, defaultAdd, defaultBoundaryPeriod, driftRows, federalHint, federalLabel, fieldLabel, isFrozenPhase, KIND_LABEL, looksLike,
  observedLabel, periodOptionLabel, programLabel, providerLabel, STANDING_OPTION_LABEL, usesComponentBoundary, writePeriod, type AddChoice, type DriftRow,
} from '../../lib/boundary-model';
import { invalidationMap, queryKeys } from '../../lib/query-keys';
import { usePrograms } from '../../lib/workspace-navigation';
import { DisclosureMenu, SplitButton } from './DisclosureMenu';
import { InfraInventoryReview } from './InfraInventoryReview';
import { BUCKETS, InfraVerdictBoard } from './InfraVerdictBoard';
import { InfraStrategyCompare } from './InfraStrategyCompare';
import { InfraPlanView } from './InfraPlanView';
import type { ToastApi } from './program-toast';
import { getVerdicts, listInventory, listPlans } from '../../lib/infra-plan-api';
import { inventoryCalloutText, inventoryProgress } from '../../lib/infra-inventory-model';
import { AskAdmin } from '../AskAdmin';
import { ResourceReveal } from '../sweep/ResourceReveal';
import { SubNav } from '../sweep/SubNav';
import { getSweepSummary, sweepKeys } from '../../lib/sweep-api';
import '../../pages/today.css';
import './component-boundary.css';

interface ToastState { message: string; details?: string; undo?: () => Promise<void> | void; action?: { label: string; onClick: () => void }; returnFocus?: HTMLElement | null; version: number }

const errMsg = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);

export function ComponentBoundary({ slug, prefix, manifest: m, card, canManage, isAdmin }: {
  slug: string; prefix: string; manifest: ProgramManifest; card: ProgramCard; canManage: boolean; isAdmin: boolean;
}) {
  const qc = useQueryClient();
  const cards = usePrograms(slug).data?.programs ?? [];
  const [sp, setSp] = useSearchParams();
  const guided = m.features.transitionGuidance === true;
  const view = sp.get('view') === 'suggestions' ? 'suggestions' : sp.get('view') === 'issues' ? 'issues' : sp.get('view') === 'inventory' && guided ? 'inventory' : sp.get('view') === 'verdicts' && guided ? 'verdicts' : sp.get('view') === 'strategies' && guided ? 'strategies' : sp.get('view') === 'plan' && guided ? 'plan' : 'components';
  const fromClassA = sp.get('from') === 'class-a';
  const label = programLabel(m.key);
  const other = cards.find((c) => c.program !== m.key && usesComponentBoundary(getProgramManifest(c.program))) ?? null;
  const otherLabel = other ? programLabel(other.program) : null;
  const today = useQuery({ queryKey: queryKeys.today(slug), queryFn: () => getToday(slug), staleTime: 60_000 });
  const canEditOther = !!other && (isAdmin || !!today.data?.viewer.lead_program_ids.includes(other.profile_id));

  // Periods: an open (pre-Audit) period by default, else the standing boundary (certified + ConMon); a frozen period is read-only.
  const periods = card.periods.filter((p) => p.active && p.period_id);
  const [periodId, setPeriodId] = useState<string | null>(() => defaultBoundaryPeriod(periods));
  const period = periods.find((p) => p.period_id === periodId) ?? null;
  const frozen = !!period && isFrozenPhase(period.phase);
  const editable = canManage && !frozen;

  const key = (k: string, ...rest: unknown[]) => ['boundary', slug, k, ...rest] as const;
  const components = useQuery({ queryKey: key('components'), queryFn: () => listComponents(slug, m.key) });
  const scope = useQuery({ queryKey: key('scope', m.key, periodId), queryFn: () => getScope(slug, m.key, periodId) });
  const otherScope = useQuery({ queryKey: key('scope', other?.program, null), queryFn: () => getScope(slug, other!.program, null), enabled: !!other });
  const suggestions = useQuery({ queryKey: key('suggestions'), queryFn: () => listSuggestions(slug, m.key) });
  const drift = useQuery({ queryKey: key('drift', m.key, periodId), queryFn: () => getDrift(slug, m.key, periodId) });
  const otherDrift = useQuery({ queryKey: key('drift', other?.program, null), queryFn: () => getDrift(slug, other!.program, null), enabled: !!other && fromClassA });
  const replacements = useQuery({ queryKey: key('replacements'), queryFn: () => listReplacements(slug, m.key) });
  const inventory = useQuery({ queryKey: ['infra-plan', slug, 'items'], queryFn: () => listInventory(slug), enabled: guided });
  // V109-5-2/5-3: the Scope sub-view tabs carry the verdict "need you" count, so the board is read on every guided view.
  const verdicts = useQuery({ queryKey: ['infra-plan', slug, 'verdicts', m.key], queryFn: () => getVerdicts(slug, m.key), enabled: guided });
  const sweep = useQuery({ queryKey: sweepKeys.summary(slug), queryFn: () => getSweepSummary(slug), enabled: guided, staleTime: 30_000, retry: false });
  const plansQ = useQuery({ queryKey: ['infra-plan', slug, 'plans', m.key], queryFn: () => listPlans(slug, m.key), enabled: guided && view === 'components', retry: false });
  const targetPlan = plansQ.data?.plans.find((p) => !p.superseded_by_plan_id) ?? null;
  const services = useQuery({ queryKey: queryKeys.programExternalServices(slug, m.key), queryFn: () => listProgramExternalServices(slug, m.key), staleTime: 60_000 });
  const names = useMemo(() => new Map((services.data?.external_services ?? []).map((s) => [s.external_service_id, s.name])), [services.data]);

  const [toast, setToast] = useState<ToastState | null>(null);
  // A toast belongs to the view that raised it: a view change hides it (its returnFocus may be gone).
  const lastView = useRef(view);
  useEffect(() => { if (lastView.current !== view) { lastView.current = view; setToast(null); } }, [view]);
  // V105-4-6 flow: `?uses=<external_service_id>` (from the verdict board) shows the components using that
  // service, focuses the first one's switch, or opens Add component prefilled with it.
  const uses = view === 'components' ? sp.get('uses') : null;
  const usesFocused = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [lastChanged, setLastChanged] = useState<{ id: string; undo: () => Promise<void> } | null>(null);
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    if (!uses || !components.data || usesFocused.current === uses) return;
    usesFocused.current = uses;
    if (!componentsUsing(uses, components.data.components).length) { if (editable) setAdding(true); return; }
    const id = window.requestAnimationFrame(() => document.querySelector<HTMLElement>('tr[data-uses] [data-switch] input')?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [uses, components.data, editable]);
  // Built-UI #5: Reveal's "Change" opens a resource's disposition here — `?component=<id>&edit=disposition`
  // scrolls to that component's row and focuses its switch; `?add=<name>&edit=disposition` (no component
  // stands for the resource yet) opens Add component with its name.
  const editComponent = view === 'components' && sp.get('edit') === 'disposition' ? sp.get('component') : null;
  const addName = view === 'components' && sp.get('edit') === 'disposition' ? sp.get('add') : null;
  const editDone = useRef<string | null>(null);
  useEffect(() => {
    const key = editComponent ?? (addName ? `add:${addName}` : null);
    if (!key || !components.data || editDone.current === key) return;
    if (addName) { editDone.current = key; if (editable) setAdding(true); return; }
    // BU-1: the guard is set inside the frame, so StrictMode's cleanup (which cancels the frame) can't eat the focus.
    const id = window.requestAnimationFrame(() => {
      const row = document.querySelector<HTMLElement>(`tr[data-component="${CSS.escape(editComponent!)}"]`);
      if (!row) return;
      editDone.current = key;
      row?.scrollIntoView?.({ block: 'center' });
      (row?.querySelector<HTMLElement>('[data-switch] input, .gp-cb-ro') ?? row)?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [editComponent, addName, components.data, editable]);
  const focusAfterToast = useRef<HTMLElement | null>(null);
  const sgHeading = useRef<HTMLHeadingElement>(null);
  // V105-3-6 (H12): Back from the inventory returns focus to the callout that opened it.
  const invCallout = useRef<HTMLButtonElement>(null);
  const backToCallout = useRef(false);
  // V105-4-6 H10: Back from the verdicts returns focus to the verdict callout.
  const verdictCallout = useRef<HTMLButtonElement>(null);
  const backToVerdictCallout = useRef(false);
  // V105-6-7 R2: Back to Scope from the target plan returns focus to the plan callout (else the heading).
  const planCallout = useRef<HTMLButtonElement>(null);
  const backToPlanCallout = useRef(false);
  useEffect(() => {
    if (view !== 'components' || !backToPlanCallout.current) return;
    backToPlanCallout.current = false;
    const id = window.requestAnimationFrame(() => (planCallout.current ?? document.getElementById('cb-h'))?.focus());
    return () => window.cancelAnimationFrame(id);
  });
  useEffect(() => {
    if (view !== 'components' || !backToVerdictCallout.current) return;
    backToVerdictCallout.current = false;
    // No callout (no components): the boundary heading.
    const id = window.requestAnimationFrame(() => (verdictCallout.current ?? document.getElementById('cb-h'))?.focus());
    return () => window.cancelAnimationFrame(id);
  });
  useEffect(() => {
    if (view !== 'components' || !backToCallout.current) return;
    backToCallout.current = false;
    const id = window.requestAnimationFrame(() => (invCallout.current ?? document.getElementById('cb-h'))?.focus());
    return () => window.cancelAnimationFrame(id);
  });
  const version = useRef(0);

  // Acceptance #2: the toast is rendered first, then focus moves (next frame), so the Toast's focusin keeps it clear.
  useEffect(() => {
    if (!toast || !focusAfterToast.current) return;
    const el = focusAfterToast.current;
    focusAfterToast.current = null;
    const id = window.requestAnimationFrame(() => el.focus());
    return () => window.cancelAnimationFrame(id);
  }, [toast]);

  const refresh = () => Promise.all([
    qc.invalidateQueries({ queryKey: ['boundary', slug] }),
    ...invalidationMap['program:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })),
  ]);
  const show = (t: Omit<ToastState, 'version'>, focus?: HTMLElement | null) => {
    version.current += 1;
    focusAfterToast.current = focus ?? null;
    setToast({ ...t, version: version.current });
  };
  const runUndo = async (fn: () => Promise<void> | void, what: string) => {
    setToast(null); setLastChanged(null);
    try { await fn(); await refresh(); show({ message: `Undone · ${what}` }); } catch (e) { setError(errMsg(e, 'Undo failed.')); }
  };

  if (components.isLoading || scope.isLoading) return <Spinner label="Loading boundary" />;
  if (components.error || scope.error || !components.data || !scope.data) {
    return <Alert variant="error"><p>The boundary could not load.</p><Button onClick={() => { void components.refetch(); void scope.refetch(); }}>Retry</Button></Alert>;
  }

  const all = components.data.components;
  const usedBy = uses ? componentsUsing(uses, all) : [];
  const usesName = uses ? names.get(uses) ?? 'this service' : null;
  const backToVerdicts = () => { const n = new URLSearchParams(sp); n.delete('uses'); n.set('view', 'verdicts'); setSp(n, { replace: false }); };
  const mine = new Map(scope.data.scope.components.map((c) => [c.component_id, c.scope_id]));
  const theirs = new Map((otherScope.data?.scope.components ?? []).map((c) => [c.component_id, c.scope_id]));
  const target = writePeriod(scope.data.scope.source, periodId);
  const reportRows = drift.data ? driftRows(drift.data.drift, names) : [];
  const openSuggestions = suggestions.data?.suggestions ?? [];
  const issueCount = reportRows.length;
  const inThis = all.filter((c) => (pending[c.component_id] ?? mine.has(c.component_id))).length;
  const inOther = all.filter((c) => theirs.has(c.component_id)).length;
  const otherHref = other ? `${prefix}/programs/${other.program}/scope` : null;
  const periodName = period ? period.label : 'standing';
  // Built-UI #2: the inventory after a sweep is Reveal only — no Boundary header, component counts or services review.
  // BU-8: the same whenever Reveal has resources (found before any sweep, e.g. from a connection's scope).
  const revealOnly = view === 'inventory' && (!!sweep.data?.run || (sweep.data?.found ?? 0) > 0);
  const ask = (verb: string) => <AskAdmin slug={slug} compact verb={verb} what={`runs ${label}`} subject={`GAC Pro ${label}: ${verb}`} />;

  // ---- scope toggles ----------------------------------------------------------------------------
  const toggle = async (c: BoundaryComponent, on: boolean, el: HTMLElement | null) => {
    setError(null);
    setPending((p) => ({ ...p, [c.component_id]: on }));
    try {
      let undo: () => Promise<void>;
      if (on) {
        const r = await addToScope(slug, m.key, { component_id: c.component_id, period_id: target });
        undo = async () => { await removeFromScope(slug, m.key, r.scope.scope_id, 'Undone in the boundary editor'); };
      } else {
        const sid = mine.get(c.component_id);
        if (!sid) return;
        await removeFromScope(slug, m.key, sid, 'Removed in the boundary editor');
        undo = async () => { await addToScope(slug, m.key, { component_id: c.component_id, period_id: target }); };
      }
      await refresh();
      const where = `${label} ${period ? periodName : 'standing'} boundary`;
      const fn = () => runUndo(undo, c.component_key);
      setLastChanged({ id: c.component_id, undo: async () => fn() });
      show({ message: `${on ? 'Added' : 'Removed'} ${c.component_key}`, details: `${c.component_key} ${on ? 'added to' : 'removed from'} the ${where}`, undo: fn, returnFocus: el,
        ...(uses ? { action: { label: 'Back to verdicts', onClick: backToVerdicts } } : {}) });
    } catch (e) {
      setError(errMsg(e, 'The boundary could not change.'));
    } finally {
      setPending((p) => { const n = { ...p }; delete n[c.component_id]; return n; });
    }
  };

  const removeEverywhere = async (ids: string[], alsoOther: boolean, returnFocus: HTMLElement | null) => {
    setError(null);
    try {
      const undos: Array<() => Promise<unknown>> = [];
      for (const id of ids) {
        const sid = mine.get(id);
        if (sid) { await removeFromScope(slug, m.key, sid, 'Removed from the boundary (drift)'); undos.push(() => addToScope(slug, m.key, { component_id: id, period_id: target })); }
        const oid = theirs.get(id);
        if (alsoOther && other && oid) { await removeFromScope(slug, other.program, oid, 'Removed from the boundary (drift)'); undos.push(() => addToScope(slug, other.program, { component_id: id, period_id: null })); }
      }
      await refresh();
      const keys = ids.map((id) => all.find((c) => c.component_id === id)?.component_key ?? id).join(', ');
      show({ message: `Removed ${keys}`, details: `${keys} removed from ${label}${alsoOther && otherLabel ? ` and ${otherLabel}` : ''}`, undo: () => runUndo(async () => { for (const u of undos) await u(); }, keys), returnFocus });
    } catch (e) { setError(errMsg(e, 'The component could not be removed.')); }
  };

  // ---- suggestions ------------------------------------------------------------------------------
  const addSuggestion = async (s: BoundarySuggestion, choice: AddChoice, hint: 'yes' | 'no' | null) => {
    const federal = hint === 'yes' ? 'handles' : hint === 'no' ? 'neither' : undefined;
    const r = await acceptSuggestion(slug, m.key, s.suggestion_id, { ...(federal ? { federal_data: federal } : {}), ...(choice === 'workspace' ? {} : { scope: { period_id: target } }) });
    let otherScopeId: string | null = null;
    if (choice === 'both' && other) otherScopeId = (await addToScope(slug, other.program, { component_id: r.component.component_id, period_id: null })).scope.scope_id;
    return async () => {
      if (otherScopeId && other) await removeFromScope(slug, other.program, otherScopeId, 'Undone in the boundary editor');
      if (r.scope) await removeFromScope(slug, m.key, r.scope.scope_id, 'Undone in the boundary editor');
      await reopenSuggestion(slug, m.key, s.suggestion_id);
    };
  };
  const one = async (s: BoundarySuggestion, choice: AddChoice, hint: 'yes' | 'no' | null) => {
    setError(null);
    try {
      const undo = await addSuggestion(s, choice, hint);
      await refresh();
      show({ message: `Added ${s.name}`, details: `${s.name}: ${addChoiceLabel(choice, label, otherLabel).toLowerCase()}`, undo: () => runUndo(undo, s.name), returnFocus: sgHeading.current }, sgHeading.current);
    } catch (e) { setError(errMsg(e, 'The suggestion could not be added.')); }
  };
  const link = async (s: BoundarySuggestion, c: BoundaryComponent) => {
    setError(null);
    try {
      await linkSuggestion(slug, m.key, s.suggestion_id, c.component_id);
      await refresh();
      show({ message: `Linked ${s.name}`, details: `${s.name} linked to ${c.component_key}`, undo: () => runUndo(async () => { await reopenSuggestion(slug, m.key, s.suggestion_id); }, s.name), returnFocus: sgHeading.current }, sgHeading.current);
    } catch (e) { setError(errMsg(e, 'The suggestion could not be linked.')); }
  };
  const dismiss = async (s: BoundarySuggestion) => {
    setError(null);
    try {
      await dismissSuggestion(slug, m.key, s.suggestion_id, 'Not part of the boundary');
      await refresh();
      show({ message: `Dismissed ${s.name}`, undo: () => runUndo(async () => { await reopenSuggestion(slug, m.key, s.suggestion_id); }, s.name), returnFocus: sgHeading.current }, sgHeading.current);
    } catch (e) { setError(errMsg(e, 'The suggestion could not be dismissed.')); }
  };
  const plan = openSuggestions.map((s) => {
    const match = looksLike(s, all);
    const hint = federalHint(s, match);
    return { s, match, hint, choice: defaultAdd(hint, !!other && canEditOther) };
  });
  const fresh = plan.filter((p) => !p.match);
  const bulk = async (force?: AddChoice) => {
    setError(null);
    const done: Array<{ name: string; choice: AddChoice; undo: () => Promise<void> }> = [];
    try {
      for (const p of fresh) {
        const choice = force === 'both' && !(other && canEditOther) ? 'this' : force ?? p.choice;
        done.push({ name: p.s.name, choice, undo: await addSuggestion(p.s, choice, p.hint) });
      }
    } catch (e) { setError(errMsg(e, 'Some suggestions could not be added.')); }
    await refresh();
    if (!done.length) return;
    const by = (c: AddChoice) => done.filter((d) => d.choice === c).map((d) => d.name);
    const details = [
      by('both').length ? `${by('both').length} to ${label} + ${otherLabel} (${by('both').join(', ')})` : '',
      by('this').length ? `${by('this').length} to ${label} only (${by('this').join(', ')})` : '',
      by('workspace').length ? `${by('workspace').length} to workspace only (${by('workspace').join(', ')})` : '',
    ].filter(Boolean).join(' · ');
    show({ message: `Added ${done.length}`, details: `Added ${details}`, undo: () => runUndo(async () => { for (const d of done) await d.undo(); }, `${done.length} restored`), returnFocus: sgHeading.current }, sgHeading.current);
  };
  const counts = { both: fresh.filter((p) => p.choice === 'both').length, this: fresh.filter((p) => p.choice === 'this').length, workspace: fresh.filter((p) => p.choice === 'workspace').length };
  const bulkLabel = `Add ${fresh.length} as suggested · ${[
    counts.both ? `${counts.both} to both` : '', counts.this ? `${counts.this} to ${label}` : '', counts.workspace ? `${counts.workspace} workspace only` : '',
  ].filter(Boolean).join(', ')}`;

  const go = (v: 'components' | 'suggestions' | 'issues' | 'inventory' | 'verdicts' | 'strategies' | 'plan', open?: { itemId: string; panel: 'coverage' }) => {
    const n = new URLSearchParams(sp);
    if (v === 'components') n.delete('view'); else n.set('view', v);
    // The strategy view's own state (family / target / swaps) and a board deep link belong to their view only.
    for (const k of ['family', 'target', 'swap', 'item', 'panel']) n.delete(k);
    if (open) { n.set('item', open.itemId); n.set('panel', open.panel); }
    setSp(n, { replace: false });
  };

  // V105-4-6 A-H1: the Scope tab's single toast host, shared with the inventory review and the verdict board.
  const toastApi: ToastApi = { show: (t) => show(t), hide: () => setToast(null) };

  const sources = [...new Map(openSuggestions.map((s) => [s.connector_kind, s])).values()];
  const ctx: EditorCtx = {
    slug, prefix, m, label, other, otherLabel, all, mine, theirs, planned: replacements.data?.replacements ?? [], canManage, canEditOther, frozen,
    refresh, show, setError, removeEverywhere, ask,
  };

  return (
    <div className="gp-cb">
      {error ? <Alert variant="error" onDismiss={() => setError(null)}>{error}</Alert> : null}

      {/* V109-5-2 (design S2): the Scope sub-views as one tab row; Inventory = Reveal, Verdicts = Foundation Check. */}
      {guided ? (() => {
        const base = `${prefix}/programs/${m.key}/scope`;
        const need = verdicts.data?.board.attention_count ?? null;
        // Built-UI #1: "N found" is the same number everywhere (resources; the clues are counted beside it in Reveal).
        const found = sweep.data?.run ? sweep.data.found : null;
        const subviews = ['components', 'inventory', 'verdicts', 'strategies', 'plan'] as const;
        const current = (subviews as readonly string[]).includes(view) ? view : '';
        return <SubNav label={`${label} scope views`} current={current} onPick={(k) => go(k as typeof subviews[number])} items={[
          { key: 'components', label: 'Components', href: base },
          { key: 'inventory', label: 'Inventory', href: `${base}?view=inventory`, count: found, countLabel: 'found' },
          { key: 'verdicts', label: 'Verdicts', href: `${base}?view=verdicts`, count: need ? `${need} need you` : null },
          { key: 'strategies', label: 'Strategies', href: `${base}?view=strategies` },
          { key: 'plan', label: 'Plan', href: `${base}?view=plan` },
        ]} />;
      })() : null}

      {fromClassA && view === 'issues' ? (
        <div className="gp-cb-strip">
          <span><b>{`${issueCount} boundary issue${issueCount === 1 ? '' : 's'} for ${label}`}</b></span>
          <Link to={`${prefix}/programs/${m.key}/prepare`}>Back to Prepare</Link>
        </div>
      ) : null}

      {revealOnly ? null : <div className="gp-cb-head">
        <h3 className="gp-card-h" id="cb-h" tabIndex={-1}>Boundary</h3>
        <div className="gp-actions">
          {periods.length ? (
            <Field id="cb-period" label="Period">
              <Select value={periodId ?? ''} onChange={(e) => setPeriodId(e.currentTarget.value || null)}>
                <option value="">{STANDING_OPTION_LABEL}</option>
                {periods.map((p) => <option key={p.period_id!} value={p.period_id!}>{periodOptionLabel(p)}</option>)}
              </Select>
            </Field>
          ) : null}
          {editable && all.length ? <Button compact onClick={() => setAdding(true)}>Add component</Button> : null}
          {all.length ? <Link className="gp-linkbtn" to={`${prefix}/programs/${m.key}/scope/move`}>Plan a government cloud move</Link> : null}
        </div>
      </div>}

      {frozen && !revealOnly ? (
        <Alert variant="info">
          <p><b>{`${period!.label} is locked for audit.`}</b> Read-only. Changes go to the standing boundary.</p>
          <Button compact variant="primary" onClick={() => setPeriodId(defaultBoundaryPeriod(periods))}>{defaultBoundaryPeriod(periods) ? 'Edit the open boundary' : 'Edit the standing boundary'}</Button>
        </Alert>
      ) : null}

      {uses && view === 'components' ? (
        <div className="gp-cb-strip">
          <span><b>{usedBy.length ? `Components that use ${usesName}` : `No component uses ${usesName} yet`}</b> <span className="gp-dim">{usedBy.length ? `switch one on to add it to the ${label} boundary` : `add one for it to the ${label} boundary`}</span></span>
          <span className="gp-actions">
            <button type="button" className="gp-cb-textbtn" onClick={() => { const n = new URLSearchParams(sp); n.delete('uses'); setSp(n, { replace: true }); }}>Show all components</button>
            <Button compact variant="ghost" onClick={backToVerdicts}>Back to verdicts</Button>
          </span>
        </div>
      ) : null}

      {adding ? <AddComponentForm slug={slug} program={m.key} initial={uses && !usedBy.length ? { key: names.get(uses) ?? '', external_service_id: uses } : addName ? { key: addName } : undefined} onCancel={() => setAdding(false)} onSaved={async (c) => {
        setAdding(false);
        try { await addToScope(slug, m.key, { component_id: c.component_id, period_id: target }); } catch (e) { setError(errMsg(e, 'Added to the register; it could not join this boundary.')); }
        await refresh();
        show({ message: `Added ${c.component_key}`, details: `${c.component_key} added to the register and the ${label} boundary`,
          ...(uses ? { action: { label: 'Back to verdicts', onClick: backToVerdicts } } : {}) });
      }} /> : null}

      {all.length === 0 && !adding && view !== 'inventory' ? (
        <EmptyState title="No components yet" description="Pull them from your cloud. Nothing to retype."
          action={<span className="gp-actions">
            <Link className="gp-linkbtn" data-variant="primary" to={`${prefix}/setup`}>Connect a cloud</Link>
            {canManage ? <Button compact variant="ghost" onClick={() => setAdding(true)}>Add a component by hand</Button> : ask('add boundary components')}
            {guided ? <Button compact variant="ghost" onClick={() => go('inventory')}>Review the services you depend on</Button> : null}
          </span>} />
      ) : null}

      {all.length > 0 && !revealOnly ? (
        <p className="gp-cb-sum" role="status" aria-atomic="true">
          <span><b>{all.length}</b> components</span><span aria-hidden="true">·</span>
          <span><b>{inThis}</b>{` in ${label}`}</span>
          {other ? <><span aria-hidden="true">·</span><span><b>{inOther}</b>{` in ${otherLabel}`}</span></> : null}
        </p>
      ) : null}

      {view === 'components' && all.length > 0 ? (
        <div className="gp-cb-callouts">
          {guided && targetPlan ? (() => {
            // V105-6-7 H7 / R2: the target plan first, full width, with the plan view's own words.
            const parts = 5 - targetPlan.missing.length;
            const signed = !!targetPlan.latest_package && targetPlan.latest_package.plan_version_sha256 === targetPlan.plan_version_sha256;
            const open = targetPlan.open_items ?? 0;
            const text = targetPlan.decision_stale ? 'Verdicts changed after this strategy was chosen'
              : targetPlan.missing.length ? `${parts} of 5 parts` : signed ? `Signed version ${targetPlan.latest_package!.version}` : 'Ready to sign';
            return <div className="gp-cb-callout gp-cb-callout--wide" data-tone={targetPlan.decision_stale || open || !signed ? 'warn' : 'info'}>
              <span><b>Target plan</b> <span className="gp-dim">{`${text}${!targetPlan.decision_stale && open ? ` · ${open} open item${open === 1 ? '' : 's'}` : ''}`}</span></span>
              <Button ref={planCallout} compact onClick={() => go('plan')}>Open the target plan</Button></div>;
          })() : null}
          {guided && inventory.data ? (() => {
            // Services first (what the product depends on), then the resources inside them; the same counts as the review.
            const p = inventoryProgress(inventory.data.items);
            const t = inventoryCalloutText(p, inventory.data.items.length);
            if (targetPlan && p.complete) return <button ref={invCallout as never} type="button" className="gp-cb-textbtn gp-cb-quiet" onClick={() => go('inventory')}>{`${t.title} · ${t.action}`}</button>;
            return <div className="gp-cb-callout" data-tone={!p.open && p.needsFederal ? 'warn' : 'info'}><span><b>{t.title}</b> <span className="gp-dim">{t.detail}</span></span>
              <Button ref={invCallout} compact onClick={() => go('inventory')}>{t.action}</Button></div>;
          })() : null}
          {openSuggestions.length ? <div className="gp-cb-callout" data-tone="info"><span><b>{`${openSuggestions.length} resource${openSuggestions.length === 1 ? '' : 's'} inside those services`}</b> <span className="gp-dim">from connected clouds</span></span>
            <Button compact onClick={() => go('suggestions')}>{`Review ${openSuggestions.length}`}</Button></div> : null}
          {issueCount ? <div className="gp-cb-callout" data-tone="warn"><span><b>{`${issueCount} issue${issueCount === 1 ? '' : 's'}`}</b> <span className="gp-dim">drift and gaps</span></span>
            <Button compact onClick={() => go('issues')}>{`Review ${issueCount}`}</Button></div> : null}
          {guided && verdicts.data?.board.items.length ? (() => {
            // V105-4-6: the board's own attention count and buckets (one split, server-side), after the inventory, resource and issue callouts.
            const b = verdicts.data.board;
            const need = b.attention_count;
            const buckets = BUCKETS
              .filter(([k]) => b.buckets[k]).map(([k, l]) => `${b.buckets[k]} ${l}`).join(' · ');
            if (targetPlan && !need) return <button ref={verdictCallout as never} type="button" className="gp-cb-textbtn gp-cb-quiet" onClick={() => { backToVerdictCallout.current = true; go('verdicts'); }}>Third-party verdicts · Open verdicts</button>;
            return <div className="gp-cb-callout" data-tone={need ? 'warn' : 'info'}>
              <span><b>{need ? `${need} third-party verdict${need === 1 ? ' needs' : 's need'} you` : 'Third-party verdicts'}</b> <span className="gp-dim">{buckets}</span></span>
              <Button ref={verdictCallout} compact onClick={() => { backToVerdictCallout.current = true; go('verdicts'); }}>{need ? 'Review verdicts' : 'Open verdicts'}</Button></div>;
          })() : null}
        </div>
      ) : null}

      {view === 'suggestions' ? (
        <Card>
          <button type="button" className="gp-cb-back" onClick={() => go('components')}>‹ Back to components</button>
          <div className="gp-cb-head">
            <h3 className="gp-card-h" id="sg-h" ref={sgHeading} tabIndex={-1}>{`Resources inside those services · ${openSuggestions.length}`}</h3>
            {editable && fresh.length ? (
              <SplitButton variant="primary" label={bulkLabel} onClick={() => void bulk()} caretLabel={`Other ways to add the ${fresh.length} new`}
                choices={[
                  { label: `Add all ${fresh.length} to the ${label} boundary`, onChoose: () => void bulk('this') },
                  ...(other && canEditOther ? [{ label: `Add all ${fresh.length} to ${label}, also add to ${otherLabel}`, onChoose: () => void bulk('both') }] : []),
                  { label: `Add all ${fresh.length} to workspace only`, onChoose: () => void bulk('workspace') },
                ]} />
            ) : null}
          </div>
          <div className="gp-cb-sources">
            {sources.map((s) => <span key={s.connector_kind} className="gp-cb-src">{`${s.provider.toUpperCase()} · seen ${observedLabel(s.last_seen_at)}`}</span>)}
            <Link to={`${prefix}/setup`}>Connect another cloud</Link>
          </div>
          {openSuggestions.length === 0 ? <EmptyState title="No new resources" action={<Button compact onClick={() => go('components')}>Back to components</Button>} /> : (
            <ul className="gp-cb-rows" aria-labelledby="sg-h">
              {plan.map(({ s, match, hint, choice }) => (
                <li key={s.suggestion_id} className="gp-cb-sg">
                  <span className="gp-cb-main">
                    <b>{s.name}</b>
                    <span className="gp-cb-meta">
                      <span>{`${KIND_LABEL[s.suggested_kind]} · ${s.resource_type}`}</span>
                      <span>{`Seen ${observedLabel(s.last_seen_at)}`}</span>
                      <span>{`Federal data · ${hint === 'yes' ? 'Yes' : hint === 'no' ? 'No' : 'Not known'}`}</span>
                      {match ? <span className="gp-cb-chip" data-tone="info">{`Looks like ${match.component_key}`}</span> : <span className="gp-cb-chip">New</span>}
                    </span>
                  </span>
                  {!editable ? <span className="gp-cb-act">{ask('add suggested components')}</span> : match ? (
                    <span className="gp-cb-act">
                      <Button compact onClick={() => void link(s, match)} aria-label={`Link to ${match.component_key} · ${s.name}`}>{`Link to ${match.component_key}`}</Button>
                      <button type="button" className="gp-cb-textbtn" onClick={() => void one(s, choice, hint)} aria-label={`Add as new · ${s.name}`}>Add as new</button>
                      <Button compact variant="ghost" onClick={() => void dismiss(s)} aria-label={`Dismiss · ${s.name}`}>Dismiss</Button>
                    </span>
                  ) : (
                    <span className="gp-cb-act">
                      <SplitButton label={addChoiceLabel(choice, label, otherLabel)} ariaLabel={`${addChoiceLabel(choice, label, otherLabel)} · ${s.name}`} onClick={() => void one(s, choice, hint)}
                        caretLabel={`More add options · ${s.name}`}
                        choices={(['both', 'this', 'workspace'] as AddChoice[]).filter((c) => c !== choice && (c !== 'both' || (!!other && canEditOther))).map((c) => ({ label: addChoiceLabel(c, label, otherLabel), onChoose: () => void one(s, c, hint) }))} />
                      {all.length ? <DisclosureMenu variant="ghost" label="Link to existing" ariaLabel={`Link to existing · ${s.name}`}
                        choices={all.filter((c) => c.kind === s.suggested_kind).slice(0, 12).map((c) => ({ label: c.component_key, onChoose: () => void link(s, c) }))} /> : null}
                      <Button compact variant="ghost" onClick={() => void dismiss(s)} aria-label={`Dismiss · ${s.name}`}>Dismiss</Button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}

      {view === 'inventory' ? (
        <ResourceReveal slug={slug} prefix={prefix} program={m.key} canManage={isAdmin} boundaryHref={`${prefix}/programs/${m.key}/scope`} />
      ) : null}
      {/* Built-UI #2: once a sweep exists the inventory is Reveal only (the older services review stays for workspaces that haven't swept). */}
      {view === 'inventory' && !revealOnly ? (
        <InfraInventoryReview slug={slug} prefix={prefix} program={m.key} scope={editable ? { period_id: target } : null}
          scopeNote={editable ? null : frozen ? 'this period is locked for audit' : 'only an owner, an admin or the program lead changes this boundary'}
          onBack={() => { backToCallout.current = true; go('components'); }} onContinue={() => go('verdicts')} onOpenVerdicts={() => go('verdicts')} toastApi={toastApi} />
      ) : null}
      {view === 'verdicts' ? <InfraVerdictBoard slug={slug} prefix={prefix} program={m.key} toastApi={toastApi} onBack={() => { backToVerdictCallout.current = true; go('components'); }} onOpenInventory={() => go('inventory')} onCompare={() => go('strategies')}
        openPanel={sp.get('item') && sp.get('panel') === 'coverage' ? { itemId: sp.get('item')!, panel: 'coverage' } : null} /> : null}
      {view === 'strategies' ? <InfraStrategyCompare slug={slug} prefix={prefix} program={m.key} toastApi={toastApi} onBack={(open) => go('verdicts', open)} onOpenInventory={() => go('inventory')} onBackToScope={() => go('components')} onOpenPlan={() => go('plan')} /> : null}
      {view === 'plan' ? <InfraPlanView slug={slug} prefix={prefix} program={m.key} toastApi={toastApi} onBack={() => go('strategies')} onOpenInventory={() => go('inventory')} onBackToScope={() => { backToPlanCallout.current = true; go('components'); }} /> : null}

      {view === 'issues' ? (
        <Card>
          <button type="button" className="gp-cb-back" onClick={() => go('components')}>‹ Back to components</button>
          <h3 className="gp-card-h" id="dr-h" tabIndex={-1}>{`Drift and gaps${fromClassA ? ` · ${label}` : ''} · ${issueCount}`}</h3>
          {drift.isLoading ? <Spinner label="Loading drift" /> : issueCount === 0 ? <EmptyState title="No drift or gaps" action={<Button compact onClick={() => go('components')}>Back to components</Button>} /> : (
            <ul className="gp-cb-rows" aria-labelledby="dr-h">
              {reportRows.map((r) => <DriftItem key={r.key} row={r} ctx={ctx} />)}
            </ul>
          )}
          {fromClassA && other && otherDrift.data ? (() => {
            const ids = new Set(reportRows.flatMap((r) => r.component_ids));
            const more = driftRows(otherDrift.data.drift, names).filter((r) => !r.component_ids.some((id) => ids.has(id))).length;
            return more ? <Link className="gp-cb-muted" to={`${otherHref}?view=issues`}>{`${more} more issue${more === 1 ? '' : 's'} affect ${otherLabel} only`}</Link> : null;
          })() : null}
        </Card>
      ) : null}

      {view === 'components' && all.length > 0 ? (
        <Card>
          <div className="gp-cb-head">
            <h3 className="gp-card-h" id="cl-h">{`Components · ${all.length}`}</h3>
            <span className="gp-dim gp-cb-note">{frozen ? 'As locked · read-only' : editable ? `Switch on to add to the ${label} ${period && !frozen ? period.label : 'standing'} boundary` : ''}</span>
            {other && otherHref ? <Link className="gp-cb-muted gp-cb-narrow" to={otherHref}>{`Edit ${otherLabel} scope in ${otherLabel}`}</Link> : null}
          </div>
          <table className="gp-table gp-cb-table" role="table" aria-labelledby="cl-h">
            <thead role="rowgroup"><tr role="row">
              <th role="columnheader" scope="col">Name</th>
              <th role="columnheader" scope="col">Kind</th>
              <th role="columnheader" scope="col">Provider</th>
              <th role="columnheader" scope="col">Federal data</th>
              <th role="columnheader" scope="col">Last observed</th>
              <th role="columnheader" scope="col">{label}</th>
              {other ? <th role="columnheader" scope="col">{otherLabel} <span className="sr-only">(read-only here)</span>{otherHref ? <Link className="gp-cb-muted" to={otherHref}>{`Edit in ${otherLabel}`}</Link> : null}</th> : null}
            </tr></thead>
            <tbody role="rowgroup">
              {(uses && usedBy.length ? usedBy : all).map((c) => {
                const on = pending[c.component_id] ?? mine.has(c.component_id);
                const flags = reportRows.filter((r) => r.component_ids.includes(c.component_id) && r.tone !== 'neutral');
                return (
                  <tr role="row" key={c.component_id} data-uses={uses && usedBy.length ? '' : undefined} data-component={c.component_id} data-editing={editComponent === c.component_id ? 'true' : undefined}>
                    <td role="cell" data-label="Name" className="gp-cb-name"><b>{c.component_key}</b> {flags.map((f) => <span key={f.key} className="gp-cb-chip" data-tone={f.tone}><span aria-hidden="true">{f.tone === 'bad' ? '✕ ' : '! '}</span>{f.chip}</span>)}</td>
                    <td role="cell" data-label="Kind">{KIND_LABEL[c.kind]}</td>
                    <td role="cell" data-label="Provider">{providerLabel(c, names)}</td>
                    <td role="cell" data-label="Federal data">{federalLabel(c.federal_data)}</td>
                    <td role="cell" data-label="Last observed">{observedLabel(c.last_observed_at)}</td>
                    <td role="cell" data-label={label} className="gp-cb-tg">
                      {editable
                        ? <Switch checked={on} label={`${c.component_key} in ${label} boundary`} onText="In" offText="Out" aria-busy={c.component_id in pending || undefined}
                            onChange={(next, e) => void toggle(c, next, e.currentTarget)} />
                        : <span className="gp-cb-ro" data-on={on ? 'true' : 'false'} tabIndex={editComponent === c.component_id ? -1 : undefined}>{on ? 'In' : 'Out'}</span>}
                      {lastChanged?.id === c.component_id ? <button type="button" className="gp-cb-textbtn" onClick={() => void lastChanged.undo()} aria-label={`Undo · ${c.component_key}`}>Undo</button> : null}
                    </td>
                    {other ? <td role="cell" data-label={otherLabel ?? ''}><span className="gp-cb-ro" data-on={theirs.has(c.component_id) ? 'true' : 'false'}>{theirs.has(c.component_id) ? 'In' : 'Out'}</span></td> : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!canManage ? ask('change the boundary') : null}
        </Card>
      ) : null}

      {/* V105-7-2 critic (Medium): one always-mounted live region for the Scope tab's toasts. */}
      <ToastLiveRegion message={toast?.message ?? null} details={toast?.details} version={toast?.version ?? 0} />
      {toast ? <Toast announce={false} message={toast.message} details={toast.details} version={toast.version}
        returnFocus={toast.returnFocus?.isConnected ? toast.returnFocus : document.getElementById('cb-h')} action={toast.action}
        onUndo={toast.undo ? () => void toast.undo!() : undefined} onDismiss={() => setToast(null)} /> : null}
    </div>
  );
}

interface EditorCtx {
  slug: string; prefix: string; m: ProgramManifest; label: string; other: ProgramCard | null; otherLabel: string | null;
  all: BoundaryComponent[]; mine: ReadonlyMap<string, string>; theirs: ReadonlyMap<string, string>; planned: ReplacementItem[];
  canManage: boolean; canEditOther: boolean; frozen: boolean;
  refresh: () => Promise<unknown>;
  show: (t: Omit<ToastState, 'version'>, focus?: HTMLElement | null) => void;
  setError: (e: string | null) => void;
  removeEverywhere: (ids: string[], alsoOther: boolean, returnFocus: HTMLElement | null) => Promise<void>;
  ask: (verb: string) => ReactNode;
}

// ---- drift rows ---------------------------------------------------------------------------------------
function DriftItem({ row, ctx }: { row: DriftRow; ctx: EditorCtx }) {
  const { slug, prefix, m, label, other, otherLabel, all, theirs, mine, canManage, frozen, refresh, show, setError, removeEverywhere, ask } = ctx;
  const comps = row.component_ids.map((id) => all.find((c) => c.component_id === id)).filter((c): c is BoundaryComponent => !!c);
  const c0 = comps[0] ?? null;
  const [open, setOpen] = useState(false);
  const actRef = useRef<HTMLSpanElement>(null);
  const tags = [label, ...(other && comps.some((c) => theirs.has(c.component_id)) ? [otherLabel!] : [])];
  const planned = c0 ? ctx.planned.filter((r) => r.component_id === c0.component_id) : [];
  const subject = row.kind === 'govramp_subprocessor_unverified' && row.external_service_id ? componentsUsing(row.external_service_id, all).find((c) => mine.has(c.component_id)) ?? null : c0;
  const removeChoices = c0 ? [
    { label: `Remove from ${label} only`, onChoose: () => void removeEverywhere(row.component_ids, false, actRef.current?.querySelector('button') ?? null) },
    ...(other && ctx.canEditOther && theirs.has(c0.component_id) ? [{ label: `Remove from ${label}, also remove from ${otherLabel}`, onChoose: () => void removeEverywhere(row.component_ids, true, actRef.current?.querySelector('button') ?? null) }] : []),
  ] : [];
  return (
    <li className="gp-cb-row">
      <span className="gp-cb-main">
        <b>{row.title}</b>
        <span className="gp-cb-meta">
          <span className="gp-cb-chip" data-tone={row.tone}>{row.tone === 'bad' ? <span aria-hidden="true">✕ </span> : row.tone === 'warn' ? <span aria-hidden="true">! </span> : null}{row.chip}</span>
          <span>{row.detail}</span>
          <span className="gp-cb-tags">{tags.map((t) => <span key={t} className="gp-cb-tag">{t}</span>)}</span>
          {planned.length ? <span className="gp-cb-chip" data-tone="info">{`Replacement planned · ${planned.length} milestone${planned.length === 1 ? '' : 's'}`}</span> : null}
        </span>
        {row.kind === 'govramp_subprocessor_unverified' ? (
          <details className="gp-cb-why"><summary>{`Why ${row.title} caps GovRAMP`}</summary>
            <span>A supplier in the boundary that holds federal data must be GovRAMP or FedRAMP verified, or the product can only reach Provisional.</span></details>
        ) : null}
        {open && row.kind === 'missing_inventory_fields' ? <FillForm ctx={ctx} items={row.items} onDone={() => setOpen(false)} /> : null}
        {open && row.kind === 'federal_data_unanswered' && c0 ? <FederalForm ctx={ctx} c={c0} onDone={() => setOpen(false)} /> : null}
        {open && row.kind === 'govramp_subprocessor_unverified' && row.external_service_id ? <VerifyForm ctx={ctx} serviceId={row.external_service_id} name={row.title} onDone={() => setOpen(false)} /> : null}
      </span>
      <span className="gp-cb-act" ref={actRef}>
        {!canManage ? ask('fix boundary issues') : (() => {
          switch (row.kind) {
            case 'govramp_subprocessor_unverified':
              return <>
                {subject ? <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/programs/${m.key}/scope/replace/${encodeURIComponent(subject.component_id)}`} aria-label={`${planned.length ? 'Open the replacement' : 'Plan a replacement'} · ${row.title}`}>{planned.length ? 'Open the replacement' : 'Plan a replacement'}</Link> : null}
                <Button compact variant="ghost" aria-expanded={open} onClick={() => setOpen(!open)} aria-label={`Add verification · ${row.title}`}>Add verification</Button>
              </>;
            case 'missing_inventory_fields':
              return <Button compact aria-expanded={open} onClick={() => setOpen(!open)} aria-label={`Fill in ${row.items.length} · missing inventory fields`}>{`Fill in ${row.items.length}`}</Button>;
            case 'federal_data_unanswered':
              return <Button compact aria-expanded={open} onClick={() => setOpen(!open)} aria-label={`Answer · ${row.title}`}>Answer</Button>;
            case 'not_observed':
              return <>
                <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/setup`} aria-label={`Check the connection · ${row.title}`}>Check the connection</Link>
                {c0 && !frozen ? <Button compact variant="ghost" aria-label={`Mark retired · ${row.title}`} onClick={async () => {
                  try { await endComponent(slug, m.key, c0.component_id, 'Retired: no longer listed by its connector'); await refresh(); show({ message: `Retired ${c0.component_key}` }); }
                  catch (e) { setError(errMsg(e, 'The component could not be retired.')); }
                }}>Mark retired</Button> : null}
              </>;
            case 'tpr_incomplete':
              return <Link className="gp-linkbtn gp-linkbtn--compact" to={`/w/${slug}/app/systems`}>Fill in</Link>;
            default:
              return <>
                {c0 && !frozen && removeChoices.length ? <DisclosureMenu label="Remove from boundary" ariaLabel={`Remove from boundary · ${row.title}`} choices={removeChoices} /> : null}
                {c0?.system_id ? <Link className="gp-linkbtn gp-linkbtn--compact" data-variant="ghost" to={`/w/${slug}/app/systems/${encodeURIComponent(c0.system_id)}`} aria-label={`Open the system · ${row.title}`}>Open the system</Link> : null}
              </>;
          }
        })()}
      </span>
    </li>
  );
}

function FillForm({ ctx, items, onDone }: { ctx: EditorCtx; items: DriftRow['items']; onDone: () => void }) {
  const { slug, m, all, refresh, show, setError } = ctx;
  const [vals, setVals] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const first = useRef<HTMLInputElement | HTMLSelectElement | null>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); if (busy) return; setBusy(true); setError(null);
    try {
      for (const g of items) {
        const attrs: Record<string, string> = {}; const body: Record<string, unknown> = {};
        for (const f of g.fields ?? []) {
          const v = (vals[`${g.component_id}:${f}`] ?? '').trim();
          if (!v) continue;
          if (f === 'virtual') body.is_virtual = v === 'yes'; else if (f === 'public') body.is_public = v === 'yes';
          else if (f === 'ip_address_or_dns_name') attrs.dns_name_or_url = v; else attrs[f] = v;
        }
        if (Object.keys(attrs).length) body.attributes = { ...(all.find((c) => c.component_id === g.component_id)?.attributes ?? {}), ...attrs };
        if (Object.keys(body).length) await updateComponent(slug, m.key, g.component_id!, body);
      }
      await refresh(); onDone(); show({ message: 'Inventory fields saved' });
    } catch (err) { setError(errMsg(err, 'The fields could not be saved.')); } finally { setBusy(false); }
  };
  let firstSet = false;
  const reff = <T extends HTMLInputElement | HTMLSelectElement>() => (el: T | null) => { if (el && !firstSet) { first.current = el; firstSet = true; } };
  return (
    <form className="gp-cb-fill" onSubmit={submit} aria-label="Missing inventory fields">
      {items.map((g) => (
        <fieldset key={g.component_id} className="gp-cb-fieldset"><legend>{g.component_key}</legend>
          {(g.fields ?? []).map((f) => {
            const id = `fill-${g.component_id}-${f}`;
            const set = (v: string) => setVals((x) => ({ ...x, [`${g.component_id}:${f}`]: v }));
            return f === 'virtual' || f === 'public'
              ? <Field key={f} id={id} label={`${fieldLabel(f)[0]!.toUpperCase()}${fieldLabel(f).slice(1)}`}><Select ref={reff<HTMLSelectElement>()} value={vals[`${g.component_id}:${f}`] ?? ''} onChange={(e) => set(e.currentTarget.value)}><option value="">Choose</option><option value="yes">Yes</option><option value="no">No</option></Select></Field>
              : <Field key={f} id={id} label={`${fieldLabel(f)[0]!.toUpperCase()}${fieldLabel(f).slice(1)}`}><Input ref={reff<HTMLInputElement>()} value={vals[`${g.component_id}:${f}`] ?? ''} onChange={(e) => set(e.currentTarget.value)} /></Field>;
          })}
        </fieldset>
      ))}
      <div className="gp-actions"><Button type="submit" variant="primary" aria-disabled={busy || undefined}>{busy ? 'Saving…' : 'Save fields'}</Button><Button variant="ghost" onClick={onDone}>Cancel</Button></div>
    </form>
  );
}

function FederalForm({ ctx, c, onDone }: { ctx: EditorCtx; c: BoundaryComponent; onDone: () => void }) {
  const { slug, m, refresh, show, setError } = ctx;
  const [v, setV] = useState('');
  const ref = useRef<HTMLSelectElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <form className="gp-inline-form" onSubmit={async (e) => {
      e.preventDefault(); if (!v) return;
      try { await updateComponent(slug, m.key, c.component_id, { federal_data: v }); await refresh(); onDone(); show({ message: `Answered for ${c.component_key}` }); }
      catch (err) { setError(errMsg(err, 'The answer could not be saved.')); }
    }}>
      <Field id={`fed-${c.component_id}`} label={`Does ${c.component_key} handle federal data?`}>
        <Select ref={ref} value={v} onChange={(e) => setV(e.currentTarget.value)}><option value="">Choose</option><option value="handles">Yes, it handles it</option><option value="impacts">It can affect it</option><option value="neither">No</option></Select>
      </Field>
      <div className="gp-actions"><Button type="submit" variant="primary">Save</Button><Button variant="ghost" onClick={onDone}>Cancel</Button></div>
    </form>
  );
}

function VerifyForm({ ctx, serviceId, name, onDone }: { ctx: EditorCtx; serviceId: string; name: string; onDone: () => void }) {
  const { slug, m, refresh, show, setError } = ctx;
  const [pkg, setPkg] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <form className="gp-inline-form" onSubmit={async (e) => {
      e.preventDefault(); if (!pkg.trim()) return;
      try { await verifyExternalService(slug, m.key, serviceId, pkg.trim()); await refresh(); onDone(); show({ message: `${name} recorded as FedRAMP authorized` }); }
      catch (err) { setError(errMsg(err, 'The verification could not be recorded.')); }
    }}>
      <Field id={`ver-${serviceId}`} label="FedRAMP package ID" hint="From the FedRAMP Marketplace listing">
        <Input ref={ref} value={pkg} onChange={(e) => setPkg(e.currentTarget.value)} />
      </Field>
      <div className="gp-actions">
        <Button type="submit" variant="primary">Record as authorized</Button>
        <a href="https://marketplace.fedramp.gov/products" target="_blank" rel="noopener noreferrer">Open the FedRAMP Marketplace <span aria-hidden="true">↗</span><span className="sr-only">(opens in a new tab)</span></a>
        <Button variant="ghost" onClick={onDone}>Cancel</Button>
      </div>
    </form>
  );
}

function AddComponentForm({ slug, program, initial, onCancel, onSaved }: {
  slug: string; program: string; onCancel: () => void; onSaved: (c: BoundaryComponent) => void | Promise<void>;
  /** Prefilled from the verdict board (`?uses=`: the service's name and its external service) or Reveal's Change (`?add=`: a resource's name). */
  initial?: { key: string; external_service_id?: string | null };
}) {
  const [key, setKey] = useState(initial?.key ?? '');
  const [fn, setFn] = useState('');
  const [kind, setKind] = useState<BoundaryComponent['kind']>('managed_service');
  const [federal, setFederal] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); if (busy) return;
    const errs: Record<string, string> = {};
    if (!key.trim()) errs.key = 'Name the component.';
    if (!fn.trim()) errs.fn = 'Say what it does.';
    setErrors(errs); if (Object.keys(errs).length) return;
    setBusy(true); setFormError(null);
    try {
      const { component } = await createComponent(slug, program, { component_key: key.trim(), function: fn.trim(), kind, ...(federal ? { federal_data: federal } : {}), ...(initial?.external_service_id ? { external_service_id: initial.external_service_id } : {}) });
      await onSaved(component);
    } catch (err) { setFormError(errMsg(err, 'The component could not be added.')); } finally { setBusy(false); }
  };
  return (
    <Card>
      <form className="gp-inline-form" onSubmit={submit} noValidate aria-labelledby="add-comp-h">
        <h3 id="add-comp-h" className="gp-card-h">Add a component</h3>
        {formError ? <Alert variant="error">{formError}</Alert> : null}
        <Field id="comp-key" label="Name" required error={errors.key}><Input ref={first} value={key} onChange={(e) => setKey(e.currentTarget.value)} /></Field>
        <Field id="comp-fn" label="What it does" required error={errors.fn}><Input value={fn} onChange={(e) => setFn(e.currentTarget.value)} /></Field>
        <Field id="comp-kind" label="Kind">
          <Select value={kind} onChange={(e) => setKind(e.currentTarget.value as BoundaryComponent['kind'])}>
            {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
        </Field>
        <Field id="comp-fed" label="Federal data">
          <Select value={federal} onChange={(e) => setFederal(e.currentTarget.value)}><option value="">Not answered</option><option value="handles">Handles it</option><option value="impacts">Can affect it</option><option value="neither">No</option></Select>
        </Field>
        <div className="gp-actions"><Button type="submit" variant="primary" aria-disabled={busy || undefined}>{busy ? 'Adding…' : 'Add component'}</Button><Button variant="ghost" onClick={onCancel}>Cancel</Button></div>
      </form>
    </Card>
  );
}
